import { PAGES } from "@dune/types";
import {
	DataTable,
	Input,
	PaginatedDataTable,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useDebounce,
	useSidebar,
} from "@dune/ui";
import { Building02, SearchSm } from "@untitled-ui/icons-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetConstructionSites } from "../../../../../api/queries/constructionSite.queries";
import { useGetGFAConstructionSites } from "../../../../../api/queries/gfa.queries";
import {
	getConstructionSitesColumns,
	getGfaColumns,
} from "../../../../constructionSites/constructionList/constructionSitesColumns";
import { usePagination } from "../../../../../hooks/usePagination";
import { isGFAConstructionSite } from "../../../../../utils/gfa/gfa.utils";

const HIDE_BROKER_COLUMN = true;

type BrokerProjectsTabProps = {
	brokerSocietyId: number;
};

const BrokerProjectsTab: React.FC<BrokerProjectsTabProps> = ({
	brokerSocietyId,
}) => {
	const navigate = useNavigate();
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 1000);
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const [activeTab, setActiveTab] = useState<"constructionSite" | "gfa">(
		"constructionSite",
	);
	const { page, limit, handlePageChange, handleLimitChange } = usePagination();

	const {
		data: constructionSitesResponse = {
			data: [],
			meta: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
				hasNextPage: false,
				hasPreviousPage: false,
			},
		},
		isLoading,
		error,
	} = useGetConstructionSites(debouncedSearch, page, limit, brokerSocietyId);

	const {
		data: gfaConstructionSites = [],
		isLoading: isLoadingGfas,
		error: errorGfas,
	} = useGetGFAConstructionSites(debouncedSearch, brokerSocietyId);

	const constructionSites = constructionSitesResponse.data;
	const paginationMeta = constructionSitesResponse.meta;

	if (error || errorGfas) {
		return (
			<div className="flex flex-col items-center justify-center py-12 text-red-600">
				Erreur lors du chargement des projets.
			</div>
		);
	}

	return (
		<div className="space-y-4 flex-1 min-h-0 flex flex-col w-full min-w-0">
			<div className="flex flex-row gap-2 w-full max-w-md">
				<Input
					type="text"
					placeholder="Recherche rapide"
					value={searchInput}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setSearchInput(e.target.value)
					}
					iconOrText={<SearchSm />}
					iconOrTextPosition="right"
				/>
			</div>
			<Tabs
				value={activeTab}
				onValueChange={(value) =>
					setActiveTab(value as "constructionSite" | "gfa")
				}
				className="flex-1 min-h-0 flex flex-col w-full min-w-0"
			>
				<TabsList>
					<TabsTrigger value="constructionSite" variant="compact">
						Chantier de construction
					</TabsTrigger>
					<TabsTrigger value="gfa" variant="compact">
						GFA
					</TabsTrigger>
				</TabsList>
				<TabsContent
					value="constructionSite"
					className="mt-4 flex-1 min-h-0 flex flex-col w-full min-w-0"
				>
					<div className="flex-1 min-h-0 w-full min-w-0 overflow-auto">
						<div
							className={
								isCollapsed ? "w-full max-w-[92vw]" : "w-full max-w-[80vw]"
							}
						>
							<PaginatedDataTable
								columns={getConstructionSitesColumns(HIDE_BROKER_COLUMN)}
								data={constructionSites}
								meta={paginationMeta}
								onPageChange={handlePageChange}
								onLimitChange={handleLimitChange}
								isLoading={isLoading}
								noResultsText="Aucun chantier trouvé"
								icon={Building02}
								variant="default-with-sort"
								onRowClick={(constructionSite) => {
									if (isGFAConstructionSite(constructionSite)) {
										navigate(
											PAGES.GFA.show.withParams(constructionSite.id.toString()),
										);
									} else {
										navigate(
											PAGES.PROJECTS.show.withParams(
												constructionSite.id.toString(),
											),
										);
									}
								}}
							/>
						</div>
					</div>
				</TabsContent>
				<TabsContent
					value="gfa"
					className="mt-4 flex-1 min-h-0 flex flex-col w-full min-w-0"
				>
					<div className="flex-1 min-h-0 w-full min-w-0 overflow-auto">
						<div
							className={
								isCollapsed ? "w-full max-w-[92vw]" : "w-full max-w-[80vw]"
							}
						>
							<DataTable
								columns={getGfaColumns(HIDE_BROKER_COLUMN)}
								data={gfaConstructionSites}
								isLoading={isLoadingGfas}
								noResultsText="Aucun GFA trouvé"
								icon={Building02}
								variant="default-with-sort"
								onRowClick={(constructionSite) => {
									if (isGFAConstructionSite(constructionSite)) {
										navigate(
											PAGES.GFA.show.withParams(constructionSite.id.toString()),
										);
									} else {
										navigate(
											PAGES.PROJECTS.show.withParams(
												constructionSite.id.toString(),
											),
										);
									}
								}}
							/>
						</div>
					</div>
				</TabsContent>
			</Tabs>
		</div>
	);
};

export default BrokerProjectsTab;
